/**
 * AgentDetails Page
 * Displays detailed information about a specific Windows Agent
 * Dark theme matching SYLink platform design
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  Activity,
  Cpu,
  HardDrive,
  Network,
  AlertCircle,
  CheckCircle,
  Zap,
  Server,
  Calendar,
  Monitor,
  Clock,
  Power,
  PowerOff,
  Copy,
  Terminal,
  Wifi,
  WifiOff
} from 'lucide-react';
import {
  getAgentById,
  getAgentJobs,
  getAgentHeartbeats,
  pingAgent,
  sendAgentCommand,
  disconnectAgent,
  enableAgent
} from '../../api/agents';
import AgentStatusBadge from '../../components/Agents/AgentStatusBadge';

const AgentDetails = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();

  const [agent, setAgent] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [heartbeats, setHeartbeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [copied, setCopied] = useState(null);

  const loadAgentData = async () => {
    try {
      setError(null);
      const [agentData, jobsData, heartbeatsData] = await Promise.all([
        getAgentById(agentId),
        getAgentJobs(agentId, { limit: 10 }).catch(() => []),
        getAgentHeartbeats(agentId, { hours: 24 }).catch(() => [])
      ]);
      setAgent(agentData);
      setJobs(Array.isArray(jobsData) ? jobsData : (jobsData?.items || []));
      setHeartbeats(Array.isArray(heartbeatsData) ? heartbeatsData : []);
    } catch (err) {
      console.error('Failed to load agent:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement de l\'agent');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgentData();
    const interval = setInterval(() => { loadAgentData(); }, 15000);
    return () => clearInterval(interval);
  }, [agentId]);

  const handlePing = async () => {
    if (agent?.status !== 'online') {
      setPingResult({ success: false, message: 'L\'agent doit être connecté pour répondre au ping.' });
      return;
    }
    setPinging(true);
    setPingResult(null);
    try {
      const result = await pingAgent(agentId);
      setPingResult({ success: true, latency: result.latency, message: `Pong reçu en ${result.latency}ms` });
    } catch (err) {
      setPingResult({ success: false, message: err.response?.data?.detail || 'Échec du ping' });
    } finally {
      setPinging(false);
    }
  };

  const handleDisconnect = async () => {
    const isDisabled = agent?.status === 'disabled';
    if (isDisabled) {
      if (!window.confirm('Réactiver cet agent ? Vous devrez relancer manuellement le processus agent sur la machine pour qu\'il se reconnecte.')) return;
      setDisconnecting(true);
      try {
        await enableAgent(agentId);
        setPingResult({ success: true, message: 'Agent réactivé — démarrez le processus agent sur la machine pour reconnecter.' });
        setTimeout(loadAgentData, 2000);
      } catch (err) {
        setPingResult({ success: false, message: err.response?.data?.detail || 'Erreur lors de la réactivation' });
      } finally {
        setDisconnecting(false);
      }
      return;
    }
    if (!window.confirm('Déconnecter définitivement cet agent ? Il sera désactivé et ne pourra PAS se reconnecter tant qu\'il n\'aura pas été réactivé.')) return;
    setDisconnecting(true);
    try {
      await disconnectAgent(agentId);
      setPingResult({ success: true, message: 'Agent déconnecté et désactivé — plus de reconnexion automatique.' });
      setTimeout(loadAgentData, 2000);
    } catch (err) {
      setPingResult({ success: false, message: err.response?.data?.detail || 'Erreur lors de la déconnexion' });
    } finally {
      setDisconnecting(false);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const getAverageMetrics = () => {
    if (heartbeats.length === 0) return { cpu: 0, memory: 0, disk: 0 };
    const sum = heartbeats.reduce(
      (acc, hb) => ({
        cpu: acc.cpu + (hb.cpu_percent || 0),
        memory: acc.memory + (hb.memory_percent || 0),
        disk: acc.disk + (hb.disk_percent || 0)
      }),
      { cpu: 0, memory: 0, disk: 0 }
    );
    return {
      cpu: Math.round(sum.cpu / heartbeats.length),
      memory: Math.round(sum.memory / heartbeats.length),
      disk: Math.round(sum.disk / heartbeats.length)
    };
  };

  const getMetricColor = (value) => {
    if (value > 80) return { bar: 'bg-red-500', text: 'text-red-400' };
    if (value > 60) return { bar: 'bg-orange-500', text: 'text-orange-400' };
    return { bar: 'bg-brand-green', text: 'text-brand-green' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-black flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-brand-green mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Chargement de l'agent...</p>
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="min-h-screen bg-brand-black">
        <div className="w-[95%] max-w-[1400px] mx-auto px-4 py-6">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-red-400" />
              <div>
                <h3 className="text-lg font-medium text-red-400">Erreur</h3>
                <p className="text-red-300 text-sm">{error || 'Agent non trouvé'}</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/agents')}
              className="mt-4 px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/30 transition-colors text-sm"
            >
              Retour à la liste
            </button>
          </div>
        </div>
      </div>
    );
  }

  const avgMetrics = getAverageMetrics();
  const isOnline = agent.status === 'online';

  // Build tunnel URL from current window origin
  const serverOrigin = window.location.origin;
  const tunnelWssUrl = serverOrigin.replace('https://', 'wss://').replace('http://', 'ws://');
  const tunnelUrl = `${tunnelWssUrl}/tunnel/${agent.uid}`;

  // Start command for Windows — run from the folder where the ZIP was extracted
  const startCmd = `python -m sylink_agent run --config ".\\appsettings.json"`;

  const metricsCards = [
    { label: 'CPU', value: avgMetrics.cpu, icon: Cpu },
    { label: 'Mémoire', value: avgMetrics.memory, icon: Activity },
    { label: 'Disque', value: avgMetrics.disk, icon: HardDrive }
  ];

  return (
    <div className="min-h-screen bg-brand-black">
      <div className="w-[95%] max-w-[1400px] mx-auto px-4 py-6">

        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/agents')}
            className="flex items-center gap-2 text-gray-400 hover:text-brand-green transition-colors mb-4 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour aux agents
          </button>

          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${isOnline ? 'bg-brand-green/10' : 'bg-gray-700/30'}`}>
                <Monitor className={`w-6 h-6 ${isOnline ? 'text-brand-green' : 'text-gray-500'}`} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">{agent.name || agent.hostname || 'Agent'}</h1>
                <p className="text-gray-500 mt-0.5 font-mono text-xs">{agent.uid}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handlePing}
                disabled={pinging || !isOnline}
                title={!isOnline ? "L'agent doit être connecté" : 'Envoyer un ping'}
                className="px-3 py-2 border border-gray-700 text-gray-300 rounded-lg hover:bg-brand-black-lighter hover:border-gray-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
              >
                <Zap className={`w-4 h-4 ${pinging ? 'animate-pulse text-yellow-400' : ''}`} />
                {pinging ? 'Ping...' : 'Ping'}
              </button>
              <button
                onClick={loadAgentData}
                className="px-3 py-2 border border-gray-700 text-gray-300 rounded-lg hover:bg-brand-black-lighter hover:border-gray-600 transition-colors flex items-center gap-2 text-sm"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden md:inline">Actualiser</span>
              </button>
            </div>
          </div>
        </div>

        {/* Ping/Action Result */}
        {pingResult && (
          <div className={`mb-6 p-3 rounded-lg border ${
            pingResult.success
              ? 'bg-brand-green/10 border-brand-green/30'
              : 'bg-red-500/10 border-red-500/30'
          }`}>
            <div className="flex items-center gap-2">
              {pingResult.success
                ? <CheckCircle className="w-4 h-4 text-brand-green flex-shrink-0" />
                : <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              }
              <p className={`text-sm ${pingResult.success ? 'text-brand-green' : 'text-red-400'}`}>
                {pingResult.message}
              </p>
            </div>
          </div>
        )}

        {/* ── TUNNEL STATUS PANEL ── */}
        <div className={`mb-6 rounded-xl border p-5 ${
          isOnline
            ? 'bg-brand-green/5 border-brand-green/30'
            : 'bg-gray-800/40 border-gray-700'
        }`}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            {/* Left: status */}
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isOnline ? 'bg-brand-green/15' : 'bg-gray-700/50'
              }`}>
                {isOnline
                  ? <Wifi className="w-6 h-6 text-brand-green" />
                  : <WifiOff className="w-6 h-6 text-gray-500" />
                }
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${
                    isOnline ? 'bg-brand-green animate-pulse' : 'bg-gray-600'
                  }`} />
                  <span className={`text-base font-semibold ${isOnline ? 'text-brand-green' : 'text-gray-400'}`}>
                    Tunnel {isOnline ? 'Actif' : 'Inactif'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5 font-mono">{tunnelUrl}</p>
              </div>
            </div>

            {/* Right: toggle button */}
            {agent?.status === 'disabled' ? (
              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="flex items-center gap-2 px-4 py-2.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-lg hover:bg-amber-500/20 transition-colors text-sm font-medium disabled:opacity-50"
              >
                <Power className="w-4 h-4" />
                {disconnecting ? 'Réactivation...' : 'Réactiver l\'agent'}
              </button>
            ) : isOnline ? (
              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="flex items-center gap-2 px-4 py-2.5 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm font-medium disabled:opacity-50"
              >
                <PowerOff className="w-4 h-4" />
                {disconnecting ? 'Déconnexion...' : 'Déconnecter l\'agent'}
              </button>
            ) : (
              <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-700/30 border border-gray-700 text-gray-500 rounded-lg text-sm font-medium cursor-default select-none">
                <Power className="w-4 h-4" />
                En attente de connexion
              </div>
            )}
          </div>

          {/* Offline instructions */}
          {!isOnline && (
            <div className="mt-4 space-y-3">
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
                Comment démarrer l'agent
              </p>

              {/* Option 1: Scheduled Task (auto) */}
              <div className="bg-brand-black/60 rounded-lg p-3 border border-gray-700/50">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-semibold text-gray-300">Option 1 — Tâche planifiée</span>
                  <span className="text-xs text-gray-600">(après installation)</span>
                </div>
                <p className="text-xs text-gray-500">
                  La tâche planifiée <span className="font-mono text-gray-400">SYLinkSecurityAgent</span> démarre
                  automatiquement au démarrage Windows. Vérifiez dans le <em>Gestionnaire des tâches planifiées</em>.
                </p>
              </div>

              {/* Option 2: Manual PowerShell */}
              <div className="bg-brand-black/60 rounded-lg p-3 border border-gray-700/50">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <Terminal className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-xs font-semibold text-gray-300">Option 2 — Démarrage manuel depuis le dossier extrait</span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(startCmd, 'start')}
                    className="p-1 text-gray-600 hover:text-gray-300 transition-colors"
                    title="Copier"
                  >
                    {copied === 'start'
                      ? <CheckCircle className="w-3.5 h-3.5 text-brand-green" />
                      : <Copy className="w-3.5 h-3.5" />
                    }
                  </button>
                </div>
                <code className="text-xs text-green-400 font-mono break-all">{startCmd}</code>
              </div>

              {/* Agent UID */}
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <span>UID :</span>
                <code className="font-mono text-gray-400">{agent.uid}</code>
                <button onClick={() => copyToClipboard(agent.uid, 'uid')} className="text-gray-600 hover:text-gray-300">
                  {copied === 'uid' ? <CheckCircle className="w-3 h-3 text-brand-green" /> : <Copy className="w-3 h-3" />}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            {
              label: 'Statut',
              icon: Activity,
              iconColor: 'text-brand-green',
              iconBg: 'bg-brand-green/10',
              borderColor: 'border-brand-green/30',
              content: <AgentStatusBadge status={agent.status} lastHeartbeat={agent.last_heartbeat} size="lg" />
            },
            {
              label: 'Système',
              icon: Server,
              iconColor: 'text-blue-400',
              iconBg: 'bg-blue-500/10',
              borderColor: 'border-blue-500/30',
              content: (
                <div>
                  <p className="text-sm text-white font-medium">{agent.hostname === 'pending' ? 'Pas encore connecté' : (agent.hostname || '-')}</p>
                  <p className="text-xs text-gray-500 mt-1">{agent.os_version || 'Windows'}</p>
                </div>
              )
            },
            {
              label: 'Réseau',
              icon: Network,
              iconColor: 'text-cyan-400',
              iconBg: 'bg-cyan-500/10',
              borderColor: 'border-cyan-500/30',
              content: (
                <div>
                  <p className="text-sm text-white font-medium font-mono">{agent.internal_ip || '-'}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {agent.communication_mode === 'tunnel' ? 'Mode tunnel WebSocket' : 'Polling'}
                  </p>
                </div>
              )
            },
            {
              label: 'Dernière connexion',
              icon: Calendar,
              iconColor: 'text-purple-400',
              iconBg: 'bg-purple-500/10',
              borderColor: 'border-purple-500/30',
              content: (
                <p className="text-sm text-white font-medium">
                  {agent.last_heartbeat
                    ? new Date(agent.last_heartbeat).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
                    : 'Jamais'}
                </p>
              )
            }
          ].map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.label} className={`bg-brand-black-light rounded-lg border ${card.borderColor} px-4 py-3`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">{card.label}</p>
                  <div className={`p-1.5 rounded-lg ${card.iconBg}`}>
                    <Icon className={`w-4 h-4 ${card.iconColor}`} />
                  </div>
                </div>
                {card.content}
              </div>
            );
          })}
        </div>

        {/* Metrics (only meaningful when agent has sent heartbeats) */}
        {heartbeats.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6">
            {metricsCards.map((metric) => {
              const Icon = metric.icon;
              const color = getMetricColor(metric.value);
              return (
                <div key={metric.label} className="bg-brand-black-light rounded-lg border border-gray-800 px-4 py-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">{metric.label}</p>
                    <Icon className="w-4 h-4 text-gray-500" />
                  </div>
                  <div className="flex items-end gap-2 mb-3">
                    <span className={`text-2xl font-bold ${color.text}`}>{metric.value}%</span>
                    <span className="text-xs text-gray-500 mb-1">moy. 24h</span>
                  </div>
                  <div className="overflow-hidden h-1.5 rounded-full bg-gray-800">
                    <div style={{ width: `${metric.value}%` }} className={`h-full rounded-full ${color.bar} transition-all duration-500`} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Recent Jobs */}
        <div className="bg-brand-black-light rounded-lg border border-gray-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-800">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" />
              Tâches Récentes
            </h2>
          </div>
          <div className="p-4">
            {jobs.length === 0 ? (
              <div className="text-center py-8">
                <div className="p-3 rounded-full bg-gray-800 w-fit mx-auto mb-3">
                  <Activity className="w-6 h-6 text-gray-600" />
                </div>
                <p className="text-gray-500 text-sm">Aucune tâche exécutée</p>
              </div>
            ) : (
              <div className="space-y-2">
                {jobs.map((job) => (
                  <div
                    key={job.id}
                    className="flex items-center justify-between p-3 bg-brand-black rounded-lg border border-gray-800/50"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white">{job.job_type}</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium border ${
                          job.status === 'completed' ? 'bg-brand-green/10 text-brand-green border-brand-green/30'
                          : job.status === 'running' ? 'bg-blue-500/10 text-blue-400 border-blue-500/30'
                          : job.status === 'failed' ? 'bg-red-500/10 text-red-400 border-red-500/30'
                          : 'bg-gray-500/10 text-gray-400 border-gray-500/30'
                        }`}>
                          {job.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 font-mono">{job.target}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">
                        {job.created_at
                          ? new Date(job.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
                          : '-'}
                      </p>
                      {job.progress !== undefined && (
                        <p className="text-xs text-gray-400 mt-1 font-medium">{job.progress}%</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDetails;
